import React from "react";
import {
  getDetailLineTotal,
  getDetailRowsByType,
  getMorningMealDetail,
} from "../utils/salaryHelpers";

const formatPayrollPeriod = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  const isMonthMarker = date.getDate() <= 25;
  const start = isMonthMarker
    ? new Date(date.getFullYear(), date.getMonth() - 1, 26)
    : new Date(date.getFullYear(), date.getMonth(), 26);
  const end = isMonthMarker
    ? new Date(date.getFullYear(), date.getMonth(), 25)
    : new Date(date.getFullYear(), date.getMonth() + 1, 25);
  const formatter = new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  return `${formatter.format(start)} - ${formatter.format(end)}`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getDetailQuantity = (detail = {}) => Number(detail.quantity || 1);

const renderDetailRows = (details = [], formatCurrency, tone = "plus") => {
  const color = tone === "minus" ? "#b42318" : "#047857";
  const prefix = tone === "minus" ? "-" : "+";

  return details.map((detail) => {
    const quantity = getDetailQuantity(detail);
    const lineTotal = getDetailLineTotal(detail);
    const amount = Number(detail.amount || 0);
    const qtyLabel = quantity > 1
      ? `<br><span class="slip-item-qty" style="font-size: 7.5px; color: #666;">${quantity} x ${formatCurrency(amount)}</span>`
      : "";

    return `
      <tr>
        <td class="slip-item-name">
          <span style="color: ${color};">${escapeHtml(detail.name || "-")}</span>${qtyLabel}
        </td>
        <td class="slip-amount" style="color: ${color};">
          ${prefix} ${formatCurrency(lineTotal)}
        </td>
      </tr>
    `;
  }).join("");
};

const getSalarySlipDetails = (salary = {}) => {
  const morningMeal = getMorningMealDetail(salary);
  const otherFacilities = getDetailRowsByType(salary, "facility", "facilities").filter((detail) => detail.id !== morningMeal?.id);
  const insurances = getDetailRowsByType(salary, "insurance", "insurances");
  const workBonuses = getDetailRowsByType(salary, "work_bonus", "work_bonuses");
  const others = getDetailRowsByType(salary, "other", "others");
  const deductions = getDetailRowsByType(salary, "deduction", "deductions");

  return {
    additions: [
      ...(morningMeal ? [morningMeal] : []),
      ...otherFacilities,
      ...insurances,
      ...workBonuses,
      ...others,
    ],
    deductions,
    insurances,
    morningMeal,
    otherFacilities,
    others,
    workBonuses,
  };
};

export const isSalarySlipLong = (salary = {}) => {
  const details = getSalarySlipDetails(salary);
  return details.additions.length + details.deductions.length >= 6;
};

export const getSalarySlipStyles = () => `
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
    body { background: #fff; font-family: 'Inter', Arial, sans-serif; font-size: 9px; line-height: 1.18; margin: 0; padding: 0; color: #1f2937; }
    .slip-container { background: #fff; box-sizing: border-box; break-inside: avoid; overflow: hidden; position: relative; padding: 0 0 3mm; }
    .slip-container:last-child { page-break-after: auto; }
    .slip-half { height: 132mm; page-break-after: auto; }
    .slip-half.slip-pair-start { border-bottom: 1px dashed #d1d5db; }
    .slip-half.slip-pair-end { page-break-after: always; }
    .slip-long { height: auto; min-height: 132mm; page-break-after: always; overflow: visible; }
    .slip-page-break { break-after: page; height: 0; page-break-after: always; }
    .salary-slip-bulk-page {
      box-sizing: border-box;
      break-after: page;
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-auto-rows: 132mm;
      gap: 3mm 5mm;
      min-height: 270mm;
      page-break-after: always;
      width: 100%;
    }
    .salary-slip-bulk-page:last-child { break-after: auto; page-break-after: auto; }
    .slip-quarter {
      border-bottom: 1px dashed #d1d5db;
      height: 132mm;
      min-height: 0;
      page-break-after: auto;
      padding-bottom: 2mm;
    }
    .slip-quarter-wide { grid-column: span 2; }
    .slip-quarter .watermark { width: 190px; opacity: 0.11; }
    .slip-quarter-wide .watermark { width: 285px; opacity: 0.13; }
    .slip-quarter .header { padding-bottom: 3px; margin-bottom: 4px; }
    .slip-quarter .brand-title { font-size: 8.6px; letter-spacing: 0; }
    .slip-quarter .brand-subtitle { font-size: 6.6px; margin-top: 1px; }
    .slip-quarter .period-label { font-size: 6.2px; }
    .slip-quarter .company-logo { width: 28px; }
    .slip-quarter .company-address { font-size: 5.4px; max-width: 78px; }
    .slip-quarter .employee-block { gap: 4px; margin-bottom: 3px; }
    .slip-quarter .employee-table { font-size: 6.1px; line-height: 1.05; max-width: 76%; }
    .slip-quarter .employee-label { min-width: 40px; width: 40px; }
    .slip-quarter .print-meta { font-size: 5.7px; line-height: 1.05; }
    .slip-quarter table { margin-bottom: 3px; }
    .slip-quarter th { font-size: 6.4px; padding: 1.8px 3px; }
    .slip-quarter td { font-size: 6.2px; padding: 1.4px 3px; }
    .slip-quarter .section-row td { font-size: 5.6px; padding: 1px 3px; }
    .slip-quarter .slip-item-name { line-height: 1.05; }
    .slip-quarter .slip-item-qty { font-size: 4.9px !important; line-height: 1; }
    .slip-quarter .total-row td { font-size: 6.8px; padding: 1.8px 3px; }
    .slip-quarter .footer { margin-top: 3px; }
    .slip-quarter .signature { width: 72px; }
    .slip-quarter .signature-title { font-size: 5.8px; margin-bottom: 8px; }
    .slip-quarter .signature-name { font-size: 5.6px; padding-top: 1px; }
    .slip-quarter .document-note { font-size: 4.8px; margin-top: 2px; }
    .slip-quarter-wide .brand-title { font-size: 10px; }
    .slip-quarter-wide .brand-subtitle { font-size: 7.5px; }
    .slip-quarter-wide .period-label { font-size: 7px; }
    .slip-quarter-wide .company-logo { width: 36px; }
    .slip-quarter-wide .company-address { font-size: 6.2px; max-width: 110px; }
    .slip-quarter-wide .employee-table { font-size: 7px; max-width: 72%; }
    .slip-quarter-wide .employee-label { min-width: 50px; width: 50px; }
    .slip-quarter-wide .print-meta { font-size: 6.5px; }
    .slip-quarter-wide th { font-size: 7px; }
    .slip-quarter-wide td { font-size: 7px; }
    .slip-quarter-wide .section-row td { font-size: 6.2px; }
    .slip-quarter-wide .slip-item-qty { font-size: 5.4px !important; }
    .slip-quarter-wide .total-row td { font-size: 7.4px; }
    .watermark {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      opacity: 0.14;
      width: 315px;
      z-index: 0;
      pointer-events: none;
    }
    .slip-content {
      background: transparent;
      position: relative;
      z-index: 1;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 2px solid #333;
      padding-bottom: 5px;
      margin-bottom: 6px;
    }
    .brand-title { color: #000; font-size: 13px; letter-spacing: .3px; line-height: 1.05; margin: 0; }
    .brand-subtitle { color: #666; font-size: 9px; font-weight: 700; margin: 2px 0 0; }
    .period-label { color: #6b7280; font-size: 8px; margin: 1px 0 0; }
    .company-logo { height: auto; margin-bottom: 1px; width: 42px; }
    .company-address { color: #666; font-size: 7px; line-height: 1.15; margin: 0; max-width: 118px; }
    .employee-block { align-items: flex-start; display: flex; gap: 8px; justify-content: space-between; margin-bottom: 5px; }
    .employee-table { font-size: 7.8px; line-height: 1.12; width: auto; max-width: 72%; }
    .employee-table td { background: transparent; border: 0; padding: .5px 0; }
    .employee-label { font-weight: 700; min-width: 56px; width: 56px; }
    .print-meta { color: #666; font-size: 7.5px; line-height: 1.15; margin: 0 0 1px; text-align: right; white-space: nowrap; }
    table { background: transparent; width: 100%; border-collapse: collapse; margin-bottom: 6px; }
    th { background: transparent; border-bottom: 1px solid #d7dee8; padding: 3px 6px; text-align: left; }
    td { background: transparent; padding: 2.5px 6px; border-bottom: 1px solid #e5e7eb; }
    .section-row td { background: transparent; border-bottom: 1px solid #cbd5e1; border-top: 1px solid #cbd5e1; color: #155ca8; font-size: 8px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; }
    .section-row.addition td { color: #047857; }
    .section-row.deduction td { color: #b42318; }
    .slip-item-name { line-height: 1.12; }
    .slip-amount { font-weight: 700; text-align: right; white-space: nowrap; }
    .total-row td { background: transparent; font-weight: bold; font-size: 10px; border-top: 2px solid #111827; }
    .footer { display: flex; justify-content: space-between; margin-top: 6px; }
    .signature { text-align: center; width: 150px; }
    .signature-title { margin: 0 0 12px; }
    .signature-name { border-top: 1px solid #333; font-weight: 700; margin: 0; padding-top: 2px; }
    .document-note { color: #777; font-size: 7px; margin-top: 5px; text-align: center; }
    @page { size: A4 portrait; margin: 12mm; }
    @media print {
      .no-print { display: none; }
      body { padding: 0; margin: 0; }
      .slip-half { height: 132mm; min-height: 0; }
      .slip-long { height: auto; min-height: 132mm; }
      .salary-slip-bulk-page {
        grid-auto-rows: 132mm;
        min-height: 270mm;
      }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
`;

export const getSalarySlipStylesV2 = () => `
  ${getSalarySlipStyles()}
  <style>
    @page { size: A4 portrait; margin: 7mm; }
    body.salary-slip-print-v2 {
      background: #fff;
      color: #111827;
      font-family: 'Inter', Arial, sans-serif;
      font-size: 10px;
      line-height: 1.2;
      margin: 0;
      padding: 0;
    }
    .salary-slip-print-v2 .salary-slip-bulk-page {
      break-after: page;
      display: grid;
      gap: 2mm 4mm;
      grid-auto-rows: 139mm;
      grid-template-columns: 1fr 1fr;
      min-height: 283mm;
      page-break-after: always;
      width: 100%;
    }
    .salary-slip-print-v2 .salary-slip-bulk-page:last-child {
      break-after: auto;
      page-break-after: auto;
    }
    .salary-slip-print-v2 .slip-quarter-v2 {
      border: 1px dashed #94a3b8;
      border-bottom: 1px dashed #94a3b8;
      box-sizing: border-box;
      height: 139mm;
      overflow: hidden;
      padding: 2.5mm;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .watermark {
      opacity: 0.08;
      width: 230px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .header {
      border-bottom: 2px solid #111827;
      margin-bottom: 5px;
      padding-bottom: 4px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .brand-title {
      font-size: 11.2px;
      letter-spacing: .1px;
      line-height: 1.08;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .brand-subtitle {
      font-size: 8.3px;
      margin-top: 2px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .period-label {
      font-size: 7.6px;
      margin-top: 2px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .company-logo {
      width: 34px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .company-address {
      font-size: 6.7px;
      line-height: 1.15;
      max-width: 105px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .employee-block {
      gap: 6px;
      margin-bottom: 5px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .employee-table {
      font-size: 7.8px;
      line-height: 1.15;
      max-width: 74%;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .employee-label {
      min-width: 54px;
      width: 54px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .print-meta {
      font-size: 7.1px;
      line-height: 1.15;
    }
    .salary-slip-print-v2 .slip-quarter-v2 table {
      margin-bottom: 5px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 th {
      font-size: 8px;
      padding: 3px 4px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 td {
      font-size: 8.2px;
      padding: 2.4px 4px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .section-row td {
      font-size: 7.3px;
      padding: 2px 4px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .slip-item-name {
      line-height: 1.12;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .slip-item-qty {
      font-size: 6.2px !important;
      line-height: 1.05;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .total-row td {
      font-size: 9.4px;
      padding: 3px 4px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .footer {
      margin-top: 8px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .signature {
      width: 115px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .signature-title {
      font-size: 7.4px;
      margin-bottom: 24px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .signature-name {
      font-size: 7.2px;
      padding-top: 2px;
    }
    .salary-slip-print-v2 .slip-quarter-v2 .document-note {
      font-size: 6.4px;
      margin-top: 5px;
    }
    @media print {
      body.salary-slip-print-v2 {
        margin: 0;
        padding: 0;
      }
      .salary-slip-print-v2 .salary-slip-bulk-page {
        grid-auto-rows: 139mm;
        min-height: 283mm;
      }
    }
  </style>
`;

export const getSalarySlipHtmlContent = (salary, formatCurrency, options = {}) => {
  if (!salary) return "";
  const periodLabel = formatPayrollPeriod(salary.date_salary);
  const {
    deductions,
    insurances,
    morningMeal,
    otherFacilities,
    others,
    workBonuses,
  } = getSalarySlipDetails(salary);
  const sizeClass = options.sizeClass || (isSalarySlipLong(salary) ? "slip-long" : "slip-half");
  const positionClass = options.positionClass || "";
  
  return `
    <div class="slip-container ${sizeClass} ${positionClass}">
      <div class="watermark">
        <img src="/Kuber.jpeg" style="width: 100%;" />
      </div>

      <div class="slip-content">
      <div class="header">
        <div>
          <h1 class="brand-title">KUBER BALI ADVENTURE</h1>
          <p class="brand-subtitle">SLIP GAJI KARYAWAN</p>
          <p class="period-label">Periode: ${periodLabel}</p>
        </div>
        <div style="text-align: right;">
          <img class="company-logo" src="/Kuber.jpeg" />
          <p class="company-address">Br. Bayad, Desa Melinggih kelod, Payangan, Gianyar, Bali</p>
        </div>
      </div>
      
      <div class="employee-block">
        <table class="employee-table">
          <tr style="border: none;">
            <td class="employee-label">Nama</td>
            <td>: ${escapeHtml(salary.user?.name || "N/A")}</td>
          </tr>
          <tr style="border: none;">
            <td class="employee-label">Departemen</td>
            <td>: ${escapeHtml(salary.department_name || salary.user?.department?.name || "-")}</td>
          </tr>
          <tr style="border: none;">
            <td class="employee-label">Jabatan</td>
            <td>: ${escapeHtml(salary.user?.job || salary.user?.position?.name || "-")}</td>
          </tr>
          <tr style="border: none;">
            <td class="employee-label">Email</td>
            <td>: ${escapeHtml(salary.user?.email || "-")}</td>
          </tr>
          <tr style="border: none;">
            <td class="employee-label">No. Rekening</td>
            <td>: ${escapeHtml(salary.user?.nomor_rekening || "-")}</td>
          </tr>
        </table>
        <div>
          <p class="print-meta">Tanggal Cetak: ${new Date().toLocaleDateString("id-ID")}</p>
          <p class="print-meta">Total Kerja: ${Number(salary.total_work || 0)} hari</p>
        </div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>DESKRIPSI</th>
            <th style="text-align: right;">JUMLAH</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Gaji Pokok</td>
            <td style="text-align: right;">${formatCurrency(salary.basic_salary)}</td>
          </tr>
          <tr class="section-row addition">
            <td colspan="2">Penambahan</td>
          </tr>
          ${morningMeal ? renderDetailRows([morningMeal], formatCurrency) : ""}
          ${renderDetailRows(otherFacilities, formatCurrency)}
          ${renderDetailRows(insurances, formatCurrency)}
          ${renderDetailRows(workBonuses, formatCurrency)}
          ${renderDetailRows(others, formatCurrency)}
          <tr class="section-row deduction">
            <td colspan="2">Pengurangan</td>
          </tr>
          ${renderDetailRows(deductions, formatCurrency, "minus")}
          <tr class="total-row">
            <td>TOTAL GAJI BERSIH</td>
            <td style="text-align: right; color: #198754;">${formatCurrency(salary.total_salary)}</td>
          </tr>
        </tbody>
      </table>
      
      <div class="footer">
        <div class="signature">
          <p class="signature-title">Karyawan,</p>
          <p class="signature-name">${escapeHtml(salary.user?.name || "N/A")}</p>
        </div>
        <div class="signature">
          <p class="signature-title">HR Department,</p>
          <p class="signature-name">Manajemen Kuber</p>
        </div>
      </div>
      
      <div class="document-note">
        <p>Dokumen ini diterbitkan secara elektronik dan merupakan bukti pembayaran yang sah.</p>
      </div>
      </div>
    </div>
  `;
};

const SalarySlipExport = ({ salary, formatCurrency }) => {
  const handleExport = () => {
    if (!salary) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Slip Gaji - ${salary.user?.name || "Employee"}</title>
          ${getSalarySlipStyles()}
        </head>
        <body>
          ${getSalarySlipHtmlContent(salary, formatCurrency)}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
  };

  return (
    <button className="dropdown-item text-success" onClick={handleExport}>
      📄 Export PDF Slip
    </button>
  );
};

export default SalarySlipExport;
